import { useSelector } from 'react-redux';
import { SettingsState, Store } from '../store/types';
import config from '../config';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { FormField } from '../api/types/form';
import { FormValues } from '../components/form/types';
import { FileMetadata } from '../api/types/cms';

export type TemplateFormType = {
    type: FormField['type'];
    name: string;
    attr?: { [key: string]: any };
    label: string;
    required: boolean;
    multiple?: boolean;
    children?: Array<TemplateFormType>;
    help?: string;
    choices?: { [value: string]: any };
};

export type ParsedContent = {
    mapping?: {
        api?: string;
        call?: string;
        args?: Array<FormValues>;
        allowArgs?: boolean;
    };
    form?: Array<TemplateFormType>;
    props?: { [key: string]: any };
};

export type TemplateBlock<T, I = string> = {
    id?: string;
    index: number;
    type: string;
    only_once?: boolean;
    time_check_items?: string;
    block_name: I;
    start_date?: I;
    end_date?: I;
    content?: T;
};

type TemplateConfig = {
    config: {
        defaultBlock: string;
        blocks: Array<TemplateBlock<ParsedContent>>;
    };
};

const LoadErrorObject: TemplateBlock<ParsedContent> = {
    id: 'errors.frontend.disconnected',
    type: 'static',
    block_name: 'errors.frontend.disconnected',
    only_once: false,
    index: 0,
};

export const useTemplateBlocks = () => {
    const { branding } = useSelector<Store, SettingsState>(
        state => state.settings,
    );

    const [loading, setLoading] = useState(false);
    const [loaded, setLoaded] = useState(false);
    const [loadingError, setLoadingError] = useState(false);
    const [response, setResponse] = useState<TemplateConfig>();

    useEffect(() => {
        if (!loading && !loaded) {
            const localTemplatesString = localStorage.getItem(
                'qlive:cms-template-cache',
            );
            if (localTemplatesString && localTemplatesString !== 'undefined') {
                const localTemplatesData = JSON.parse(localTemplatesString);
                setResponse(localTemplatesData);
                setLoaded(true);
                setLoadingError(false);
            } else {
                if (branding?.frontendInfoEndpoint) {
                    loadBlockConfig();
                }
            }
        }
    }, [response]);

    const loadBlockConfig = async () => {
        const url = branding?.frontendInfoEndpoint;
        if (url) {
            setLoading(true);
            try {
                const response = await fetch(url, {
                    method: 'GET',
                    headers: {
                        Authorization: 'QClient ' + config.clientId,
                    },
                    mode: 'cors',
                });
                if (response.ok) {
                    const data = await response.json();
                    if (data) {
                        localStorage.setItem(
                            'qlive:cms-template-cache',
                            JSON.stringify(data),
                        );
                        setLoadingError(false);
                        setResponse(data);
                        setLoaded(true);
                    }
                } else {
                    setLoadingError(true);
                }
                setLoading(false);
            } catch (e) {
                setLoading(false);
                setLoadingError(true);
                return null;
            }
        }
    };

    const updateBlockConfig = (
        oldBlock: TemplateBlock<ParsedContent>,
        force: boolean = false,
    ): Promise<TemplateBlock<ParsedContent> | null> => {
        return new Promise(resolve => {
            if (force) {
                loadBlockConfig().then(() =>
                    resolve(updateBlockConfig(oldBlock)),
                );
            } else {
                const freshBlock = response?.config.blocks.find(
                    item => item.block_name === oldBlock.block_name,
                );
                let newBlock = Object.assign({}, freshBlock);
                if (newBlock) {
                    newBlock.id = oldBlock.id;
                    newBlock.index = oldBlock.index;
                    newBlock.start_date = oldBlock.start_date;
                    newBlock.end_date = oldBlock.end_date;
                    if (oldBlock.content && newBlock.content) {
                        newBlock.content = {
                            ...oldBlock.content,
                            form: newBlock.content.form,
                            mapping: {
                                ...oldBlock.content.mapping,
                                ...newBlock.content.mapping,
                            },
                            props: {
                                ...oldBlock.content.props,
                                ...newBlock.content.props,
                            },
                        };
                    }
                    resolve(newBlock);
                    return;
                }
                resolve(null);
            }
            return;
        });
    };

    return useMemo(
        () => ({
            loaded,
            loadingError,
            defaultBlock: loadingError
                ? 'errors.frontend.disconnected'
                : response?.config.defaultBlock ?? 'cms.form.block.headline',
            blocks: loadingError
                ? [LoadErrorObject]
                : response?.config.blocks ?? [],
            updateBlockConfig,
        }),
        [loaded, loadingError, response],
    );
};

export const useMedia = (
    queries: string[],
    values: number[],
    defaultValue: number,
): number => {
    const get = () =>
        values[queries.findIndex(q => matchMedia(q).matches)] ?? defaultValue;

    const [value, setValue] = useState<number>(get);

    useEffect(() => {
        const handler = () => setValue(get);
        queries.forEach(q => matchMedia(q).addEventListener('change', handler));
        return () =>
            queries.forEach(q =>
                matchMedia(q).removeEventListener('change', handler),
            );
    }, [queries]);

    return value;
};

export const useMeasure = <T extends HTMLElement>() => {
    const ref = useRef<T | null>(null);
    const [size, setSize] = useState({ width: 0, height: 0 });

    useLayoutEffect(() => {
        if (!ref.current) return;
        const ro = new ResizeObserver(([entry]) => {
            const { width, height } = entry.contentRect;
            setSize({ width, height });
        });
        ro.observe(ref.current);
        return () => ro.disconnect();
    }, []);

    return [ref, size] as const;
};

export const preloadImages = async (urls: string[]): Promise<void> => {
    await Promise.all(
        urls.map(
            src =>
                new Promise<void>(resolve => {
                    const img = new Image();
                    img.src = src;
                    img.onload = img.onerror = () => resolve();
                }),
        ),
    );
};

export function getActiveFileVersion(
    versions: Array<FileMetadata>,
): FileMetadata | null {
    const now = new Date();
    const filteredItems = versions.filter(i => i.enabled);
    for (const filteredItem of filteredItems) {
        let start,
            end = null;
        if (filteredItem.start_date) {
            start = new Date(filteredItem.start_date);
        }
        if (filteredItem.end_date) {
            end = new Date(filteredItem.end_date);
        }
        if (
            filteredItem.uses_automatic_publishing &&
            start &&
            now.getTime() >= start.getTime() &&
            end &&
            now.getTime() <= end.getTime()
        ) {
            return filteredItem;
        }
    }
    return (
        filteredItems.sort((a, b) => (a.version > b.version ? -1 : 1))?.at(0) ??
        null
    );
}

export function makeId(length: number) {
    let result = '';
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';
    const charactersLength = characters.length;
    let counter = 0;
    while (counter < length) {
        result += characters.charAt(
            Math.floor(Math.random() * charactersLength),
        );
        counter += 1;
    }
    return result;
}

export const reorder = <T>(
    list: T[],
    startIndex: number,
    endIndex: number,
): T[] => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);

    return result;
};

export const reindex = (list: Array<any>): Array<any> => {
    let index = 0;
    for (const item of list) {
        item.index = index;
        index++;
    }
    return list;
};
